import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, switchMap } from 'rxjs';

// primeNg
import { PasswordModule } from 'primeng/password';

// services
import { AuthService } from '@ui-nuvertos/auth';
import { ToasterService } from '@ui-nuvertos/auth';

@Component({
  selector: 'login',
  imports: [CommonModule, ReactiveFormsModule, PasswordModule],
  standalone: true,
  templateUrl: './login.html',
  styleUrl: './login.scss',
})

export class Login implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  loading = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toasterService: ToasterService,
    private router: Router
  ) {

    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.maxLength(64)
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Clear any redirect flag that might have been set during 401 redirect
    sessionStorage.removeItem('auth_redirecting');
    
    this.authService.loadUserSession()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user) {
            this.router.navigate(['/settings']);
          }
        },
        error: (err) => {
          console.error('Error loading user session:', err);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }
  // Method to call login API and validate
  onLogin(): void {
    if (this.loginForm.invalid) {
      this.toasterService.error('Login failed', 'Invalid credentials');
      return;
    }

    this.loading = true;
    const { email, password } = this.loginForm.value;
    this.authService.login(email, password)
      .pipe(
        switchMap(() => this.authService.loadUserSession()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (user) => {
          this.loading = false;
          this.toasterService.success('Login successful', 'Welcome!');
          // Navigate to your target route, e.g., dashboard
          this.router.navigate(['/settings']);
        },
        error: (err) => {
          this.loading = false;
          // Provide more specific error messages
          const errorMessage = err?.error?.error || err?.message || 'Invalid email or password';
          if (err?.status === 401 || err?.status === 403) {
            this.toasterService.error('Authentication Failed', errorMessage);
          } else if (err?.status === 0 || err?.status >= 500) {
            this.toasterService.error('Server Error', 'Unable to connect to server. Please try again.');
          } else {
            this.toasterService.error('Login Failed', errorMessage);
          }
        }
      });
  }

}
